import type {
  ContentStep,
  ContentStepWritable,
} from '@cfa/pathway-fe-shared/xp';

import Constants from '@/constants';

/**
 * `ContentStep` is a discriminated union — `document`, `quiz`, and `task`
 * steps each carry their own fields. These accessors read a field that only
 * exists on one member and return `undefined` for the others, so callers that
 * handle a step of unknown kind don't each need their own narrowing.
 *
 * Prefer narrowing on `step.type` directly when a call site already knows (or
 * needs to branch on) which kind of step it has.
 */
type AnyContentStep = ContentStep | ContentStepWritable;

const isDocument = (step: AnyContentStep) =>
  step.type === Constants.STEP_TYPES.DOCUMENT;

/** Hydrated resource — document steps only. */
export const getStepResource = (step: AnyContentStep | undefined) =>
  step && isDocument(step) ? step.resource : undefined;

/**
 * Whether the step's resource resolved — document steps only, and only set
 * when the plan was fetched resource-hydrated.
 */
export const getStepAvailable = (step: AnyContentStep | undefined) =>
  step && isDocument(step) && 'available' in step ? step.available : undefined;

/** Quiz id — quiz steps only. */
export const getStepQuizId = (step: AnyContentStep | undefined) =>
  step?.type === Constants.STEP_TYPES.QUIZ ? step.quizId : undefined;

/**
 * Plan-step Knowledge Quiz marker (`quizV2`). Distinct from quiz-entity `v2`.
 * Present on GET plan steps only — omitted from writable quiz steps.
 */
export const getStepQuizV2 = (step: AnyContentStep | undefined): boolean =>
  step?.type === Constants.STEP_TYPES.QUIZ &&
  'quizV2' in step &&
  step.quizV2 === true;

/** Localized note — task steps only. */
export const getStepNote = (step: AnyContentStep | undefined) =>
  step?.type === Constants.STEP_TYPES.TASK ? step.note : undefined;

/** Localized url sets — task steps only. */
export const getStepUrls = (step: AnyContentStep | undefined) =>
  step?.type === Constants.STEP_TYPES.TASK ? step.urls : undefined;
